import { useEffect, useRef, useState } from "react";
import { Select, Button, Dropdown } from "antd";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  ChevronsDown,
  ChevronsUp,
  Plus,
  Sparkles,
  Upload,
  Minus,
  Save,
  ChevronDown,
  File,
  FileText,
  Files,
  Space,
} from "lucide-react";

import PersonalInfo from "./pdf/sections/PersonalInfo.jsx";
import Summary from "./pdf/sections/Summary.jsx";
import Education from "./pdf/sections/Education.jsx";
import Skills from "./pdf/sections/Skills.jsx";
import Projects from "./pdf/sections/Projects.jsx";
import Experience from "./pdf/sections/Experience.jsx";
import Certifications from "./pdf/sections/Certifications.jsx";
import Achievements from "./pdf/sections/Achievements.jsx";
import Languages from "./pdf/sections/Languages.jsx";
import Publications from "./pdf/sections/Publications.jsx";
import Interests from "./pdf/sections/Interests.jsx";
import {
  RESUME_SECTION_OPTIONS,
  RESUME_SECTION_TYPES,
} from "./editor/config.js";
import WebUtils from "../../../../utils/WebUtils.js";
import Spacer from "./pdf/sections/Spacer.jsx";

const RENDERERS = {
  [RESUME_SECTION_TYPES.PERSONAL_INFO]: PersonalInfo,
  [RESUME_SECTION_TYPES.SUMMARY]: Summary,
  [RESUME_SECTION_TYPES.EXPERIENCE]: Experience,
  [RESUME_SECTION_TYPES.EDUCATION]: Education,
  [RESUME_SECTION_TYPES.SKILLS]: Skills,
  [RESUME_SECTION_TYPES.PROJECTS]: Projects,
  [RESUME_SECTION_TYPES.CERTIFICATIONS]: Certifications,
  [RESUME_SECTION_TYPES.ACHIEVEMENTS]: Achievements,
  [RESUME_SECTION_TYPES.LANGUAGES]: Languages,
  [RESUME_SECTION_TYPES.INTERESTS]: Interests,
  [RESUME_SECTION_TYPES.SPACER]: Spacer,

  // [RESUME_SECTION_TYPES.PUBLICATIONS]: Publications,
};

export default function SectionList({
  sections,
  sectionData,
  allCollapsed,
  updateSectionData,
  addSection,
  removeSection,
  reorderSection,
  toggleCollapseAll,
  setPreviewOpen,
  handleSaveAsDraft,
  setSubmitModal,
  setSaveCopyModal,
  resumeIdentifier,
  //   onUploadClick,
  //   onGenerateClick,
}) {
  const [pendingType, setPendingType] = useState(undefined);
  const scrollContainerRef = useRef(null);
  const prevSectionsLengthRef = useRef(sections?.length ?? 0);

  useEffect(() => {
    const currentLength = sections?.length ?? 0;
    if (currentLength > prevSectionsLengthRef.current) {
      requestAnimationFrame(() => {
        const el = scrollContainerRef.current;
        if (el) {
          el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
        }
      });
    }
    prevSectionsLengthRef.current = currentLength;
  }, [sections?.length]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = ({ active, over }) => {
    if (!over) return;
    reorderSection(active.id, over.id);
  };

  const handleAdd = () => {
    if (!pendingType) return;
    addSection(pendingType);
    setPendingType(undefined);
  };

  const experiences = (sections ?? [])
    .filter((s) => s.type === RESUME_SECTION_TYPES.EXPERIENCE)
    .flatMap((s) => sectionData?.[s.id] ?? []);

  const projects = (sections ?? [])
    .filter((s) => s.type === RESUME_SECTION_TYPES.PROJECTS)
    .flatMap((s) => sectionData?.[s.id] ?? []);

  const existingTypes = new Set((sections ?? []).map((s) => s.type));
  const availableSectionOptions = RESUME_SECTION_OPTIONS.filter(
    (opt) =>
      opt.value === RESUME_SECTION_TYPES.SPACER || !existingTypes.has(opt.value),
  );

  const saveItems = [
    {
      key: "draft",
      label: (
        <div className="flex items-center gap-2 text-[.85rem] p-[.25rem]">
          <Save size={18} />
          Save as draft
        </div>
      ),
    },
    {
      key: "submit",
      label: (
        <div className="flex items-center gap-2 text-[.85rem] p-[.25rem]">
          <FileText size={18} />
          Finalize & Save
        </div>
      ),
    },
    {
      key: "copy",
      label: (
        <div className="flex items-center gap-2 text-[.85rem] p-[.25rem]">
          <Files size={18} />
          Save as another copy
        </div>
      ),
    },
  ];

  return (
    <div className="max-w-[900px] mx-auto flex flex-col h-full">
      <div className="mt-2 flex justify-between flex-wrap items-center gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Select
            placeholder="Select section"
            value={pendingType}
            onChange={setPendingType}
            options={availableSectionOptions}
            style={{ width: 200 }}
            allowClear
          />
          <button
            className="btn-orange py-[.35rem] text-[.85rem] px-3 rounded-[6px] disabled:opacity-40 flex gap-2 items-center"
            onClick={handleAdd}
            disabled={!pendingType}
          >
            <Plus size={14} /> Add Section
          </button>
          <Button
            icon={<Space size={14} />}
            onClick={() => addSection(RESUME_SECTION_TYPES.SPACER)}
            title="Insert an adjustable vertical gap"
            className="text-[#667085] text-[.875rem] font-medium"
          >
            Add Spacer
          </Button>
          <Button
            icon={
              allCollapsed ? (
                <ChevronsDown size={14} />
              ) : (
                <ChevronsUp size={14} />
              )
            }
            onClick={toggleCollapseAll}
            title={allCollapsed ? "Expand all" : "Collapse all"}
          >
            {/* {allCollapsed ? "Expand all" : "Collapse all"} */}
          </Button>
        </div>
        <div className="ml-auto flex gap-2">
          <Dropdown
            menu={{
              items: saveItems,
              onClick: ({ key }) => {
                if (key === "draft") {
                  handleSaveAsDraft();
                }
                if (key === "submit") {
                  setSubmitModal(true);
                }
                if (key === "copy") {
                  setSaveCopyModal(true);
                }
              },
            }}
            trigger={["click"]}
            placement="bottomLeft"
            overlayClassName="bulk-actions-dropdown"
            className="px-2 rounded-[6px]"
          >
            <button
              className="flex items-center gap-[.5rem] text-[#1D2939] hover:brightness-[1.1] transition-all duration-200 ease-in-out 
            px-[.9rem] py-[.42rem] border-[1px] border-[#EDC043] rounded-[6px] bg-[#EDC043]"
            >
              <span className="flex gap-3 items-center">
                <span className="font-semibold text-[.82rem] flex gap-[.5rem] items-center">
                  Save Options
                </span>
              </span>
              <span className="">
                <ChevronDown size={19} />
              </span>
            </button>
          </Dropdown>
          {/* <button
            className="btn-orange py-[.35rem] text-[.82rem] px-3 rounded-[6px] flex gap-[.45rem] items-center"
            onClick={handleSave}
          >
            {resumeIdentifier ? (
              <>
                <Save size={15} /> Save Changes
              </>
            ) : (
              <>
                <Plus size={15} /> Create Resume
              </>
            )}
          </button> */}
          {/* <Button icon={<Upload size={14} />} onClick={onUploadClick}>
            Upload Resume
          </Button>
          <Button
            icon={<Sparkles size={14} />}
            onClick={onGenerateClick}
            type="default"
          >
            Generate from scratch
          </Button> */}
        </div>
        <div className="block lg:hidden">
          <button
            onClick={() => setPreviewOpen(true)}
            className="btn-orange py-[.35rem] text-[.85rem] px-4 rounded-[6px] flex gap-2 items-center"
          >
            Preview <FileText size={15} />
          </button>
        </div>
      </div>

      {sections?.length > 0 ? (
        <div
          ref={scrollContainerRef}
          className="flex-1 max-h-[82vh] overflow-y-auto pr-1 mt-3"
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sections.map((s) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              {sections.map((s) => {
                const Renderer = RENDERERS[s.type];
                if (!Renderer) return null;
                const extraProps =
                  s.type === RESUME_SECTION_TYPES.SUMMARY
                    ? { experiences, projects }
                    : {};
                return (
                  <Renderer
                    key={s.id}
                    id={s.id}
                    data={sectionData[s.id]}
                    onChange={(data) => updateSectionData(s.id, data)}
                    onDelete={removeSection}
                    allCollapsed={allCollapsed}
                    {...extraProps}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
        </div>
      ) : (
        <div className="mt-12 text-center text-gray-400 font-normal text-[.95rem]">
          No sections added. Start adding sections.
        </div>
      )}
    </div>
  );
}
